/**
 * WebAuthn speaks ArrayBuffer; JSON speaks base64url. These convert between the two, so no extra
 * dependency is needed for what is a handful of lines either way.
 */

const base64UrlToBytes = (value) => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

const bytesToBase64Url = (buffer) => {
  const binary = String.fromCharCode(...new Uint8Array(buffer));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * Passkeys need a secure context, which the farm LAN (plain http over an IP) is not — there
 * `navigator.credentials` simply does not exist. Callers use this to hide the option rather than
 * offer a button that cannot work.
 */
export const isPasskeySupported = () =>
  Boolean(window.isSecureContext && window.PublicKeyCredential && navigator.credentials?.create);

/** The user dismissed the system prompt: expected, and not worth an error message. */
export const isPasskeyCancelled = (error) =>
  error?.name === 'NotAllowedError' || error?.name === 'AbortError';

export const createCredential = async (options) => {
  const credential = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: base64UrlToBytes(options.challenge),
      user: { ...options.user, id: base64UrlToBytes(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map((descriptor) => ({
        ...descriptor,
        id: base64UrlToBytes(descriptor.id),
      })),
    },
  });

  return {
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bytesToBase64Url(credential.response.clientDataJSON),
      attestationObject: bytesToBase64Url(credential.response.attestationObject),
      transports: credential.response.getTransports?.() || [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
};

export const getCredential = async (options) => {
  const credential = await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: base64UrlToBytes(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((descriptor) => ({
        ...descriptor,
        id: base64UrlToBytes(descriptor.id),
      })),
    },
  });

  return {
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bytesToBase64Url(credential.response.clientDataJSON),
      authenticatorData: bytesToBase64Url(credential.response.authenticatorData),
      signature: bytesToBase64Url(credential.response.signature),
      // Absent for a non-discoverable credential; the server then has nothing to resolve.
      userHandle: credential.response.userHandle
        ? bytesToBase64Url(credential.response.userHandle)
        : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
};

/** Suggests a label so the farmer is not asked to name a key they just tapped through. */
export const suggestPasskeyLabel = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Ten klucz';
};
